/**
 * use-research-sources — Hook séparé pour grouper les sources de recherche
 * par plateforme (YouTube / Scholar / TikTok / Instagram / X / Articles).
 *
 * ⚠️ Contrainte S30 : use-deliberation.ts NE DOIT PAS être modifié.
 * Ce hook est une DÉRIVATION pure au-dessus de `state.investigationSources`
 * exposé par useDeliberation. Il peut, à terme, être muté en hook SSE
 * autonome sans casser sa surface publique (`useResearchSources(input)`).
 *
 * Architecture :
 *   const state = useDeliberation(id);
 *   const sources = useResearchSources(state.investigationSources, state.investigationProgress);
 */
import { useMemo } from "react";
import type {
  InvestigationProgress,
  InvestigationSource,
} from "@/components/Deliberation/InvestigationLive";

export type SourcePlatform = "youtube" | "scholar" | "tiktok" | "instagram" | "pinterest" | "x" | "linkedin" | "juridique" | "article";

export interface PlatformBucket {
  platform: SourcePlatform;
  label: string;
  count: number;
  items: InvestigationSource[];
}

export interface ResearchSourcesResult {
  buckets: PlatformBucket[];
  total: number;
  isEmpty: boolean;
  /** Pipeline recherche terminée (research_progress.status === "complete"). */
  isCompleted: boolean;
}

const PLATFORM_LABEL: Record<SourcePlatform, string> = {
  youtube: "YouTube",
  scholar: "Scholar",
  tiktok: "TikTok",
  instagram: "Instagram",
  pinterest: "Pinterest",
  x: "X",
  linkedin: "LinkedIn",
  juridique: "Juridique",
  article: "Articles",
};

/** Classification par URL (priorité) puis catégorie backend. */
function urlToPlatform(url: string): SourcePlatform | null {
  if (!url) return null;
  let host = "";
  try {
    host = new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    host = url.toLowerCase();
  }
  if (host.includes("youtube.com") || host.includes("youtu.be")) return "youtube";
  if (host.includes("tiktok.com")) return "tiktok";
  if (host.includes("instagram.com")) return "instagram";
  if (host.includes("pinterest.com") || host.includes("pinterest.fr") || host.includes("pin.it")) return "pinterest";
  if (host === "x.com" || host.endsWith(".x.com") || host.includes("twitter.com") || host.includes("t.co")) return "x";
  if (host.includes("linkedin.com") || host.includes("lnkd.in")) return "linkedin";
  if (host.includes("scholar.google") || host.includes("semanticscholar.org") || host.includes("arxiv.org") || host.includes("ssrn.com") || host.includes("researchgate")) return "scholar";
  if (host.includes("legifrance.gouv.fr") || host.includes("judilibre") || host.includes("courdecassation.fr")) return "juridique";
  return null;
}

function categoryToPlatform(category: string): SourcePlatform {
  const c = (category || "").toLowerCase();
  if (c.includes("youtube")) return "youtube";
  if (c.includes("tiktok")) return "tiktok";
  if (c.includes("instagram")) return "instagram";
  if (c.includes("pinterest")) return "pinterest";
  if (c.includes("social_x") || c.includes("x_") || c === "x" || c.includes("twitter")) return "x";
  if (c.includes("linkedin")) return "linkedin";
  if (c.includes("juridique") || c.includes("jurisprudence")) return "juridique";
  if (
    c.includes("scholar") ||
    c.includes("academic") ||
    c.includes("paper") ||
    c.includes("rapport") ||
    c.includes("reglementaire")
  ) {
    return "scholar";
  }
  return "article";
}

/** Indices texte (titre + metadata) pour rattraper les vanity-domains
 *  (ex: priscillavettese.com qui est en fait un compte Instagram). */
function hintsToPlatform(src: InvestigationSource): SourcePlatform | null {
  const haystack = [
    src.title,
    src.metadata?.channel,
    src.metadata?.domain,
    (src.metadata as Record<string, unknown> | undefined)?.handle as string | undefined,
    (src.metadata as Record<string, unknown> | undefined)?.platform as string | undefined,
    (src.metadata as Record<string, unknown> | undefined)?.source as string | undefined,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (!haystack) return null;
  // @handle isolé = signal social fort ; on l'oriente Instagram par défaut
  // si rien d'autre ne tranche (le casting Eva cite surtout IG).
  if (/\binstagram\b|\big\b|\binsta\b/.test(haystack)) return "instagram";
  if (/\bpinterest\b|\bpin\b/.test(haystack)) return "pinterest";
  if (/\btiktok\b|\btt\b/.test(haystack)) return "tiktok";
  if (/\byoutube\b|\byt\b/.test(haystack)) return "youtube";
  if (/\b(twitter|x\.com|x post|tweet)\b/.test(haystack)) return "x";
  if (/\blinkedin\b|\bli\b/.test(haystack)) return "linkedin";
  if (/\bscholar\b|\barxiv\b|\bdoi\b|\bjournal\b/.test(haystack)) return "scholar";
  if (/@[a-z0-9._]{2,}/.test(haystack)) return "instagram";
  return null;
}

function classifySource(src: InvestigationSource): SourcePlatform {
  // Priorité : plateforme dérivée du type d'event SSE (use-deliberation),
  // puis host d'URL, puis indices texte, puis catégorie backend.
  if (src.platform && (ORDER as readonly string[]).includes(src.platform)) {
    return src.platform as SourcePlatform;
  }
  return (
    urlToPlatform(src.url) ??
    hintsToPlatform(src) ??
    categoryToPlatform(src.category || "article")
  );
}

const ORDER: SourcePlatform[] = ["youtube", "scholar", "tiktok", "instagram", "pinterest", "x", "linkedin", "juridique", "article"];

export function useResearchSources(
  sources: InvestigationSource[] | undefined,
  progress?: InvestigationProgress,
): ResearchSourcesResult {
  return useMemo(() => {
    const safe = Array.isArray(sources) ? sources : [];
    const groups = new Map<SourcePlatform, InvestigationSource[]>();
    for (const platform of ORDER) groups.set(platform, []);

    for (const src of safe) {
      const platform = classifySource(src);
      groups.get(platform)!.push(src);
    }

    // Tri intra-plateforme : plus récent en premier
    for (const items of groups.values()) {
      items.sort((a, b) => (b.receivedAt ?? 0) - (a.receivedAt ?? 0));
    }

    const buckets: PlatformBucket[] = ORDER.map((platform) => ({
      platform,
      label: PLATFORM_LABEL[platform],
      count: groups.get(platform)!.length,
      items: groups.get(platform)!,
    }));

    const total = progress?.total ?? safe.length;
    const isEmpty = safe.length === 0;
    const isCompleted = progress?.status === "complete";

    return { buckets, total, isEmpty, isCompleted };
  }, [sources, progress]);
}
